import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

interface SystemNotificationToastProps {
  notification: any;
  onClose: () => void;
  onMarkAsRead: () => void;
  duration?: number;
}

const SystemNotificationToast: React.FC<SystemNotificationToastProps> = ({
  notification,
  onClose,
  onMarkAsRead,
  duration = 8000
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isHovered) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Wait for fade out animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, isHovered, onClose]);

  const handleAction = () => {
    // Mark as read
    onMarkAsRead();

    // Handle different notification actions
    if (notification.data?.action === 'new_errand' && notification.data?.errand_id) {
      // Navigate to driver errands page to see the new errand
      navigate('/driver/errands');
    } else if (notification.data?.action === 'bill_user' && notification.data?.escrow_request_id) {
      // Navigate to admin escrow management
      navigate(`/admin/escrow`);
    } else if (notification.data?.action === 'pay_bill' && notification.data?.escrow_request_id) {
      // Navigate to user escrow bills
      navigate('/escrow/bills');
    } else if (notification.data?.action === 'payment_received') {
      // Navigate to courier earnings
      navigate('/courier/earnings');
    } else if (notification.data?.action === 'errand_accepted' && notification.data?.errand_id) {
      // Navigate to chat for accepted errand
      navigate(`/chat?errand=${notification.data.errand_id}`);
    } else if (notification.data?.action === 'errand_completed' && notification.data?.errand_id) {
      // Navigate to errand details
      navigate(`/errand/${notification.data.errand_id}`);
    }

    onClose();
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const getActionText = (action: string) => {
    switch (action) {
      case 'new_errand': return 'View Errand';
      case 'bill_user': return 'View Escrow';
      case 'pay_bill': return 'Pay Bill';
      case 'payment_received': return 'View Earnings';
      case 'errand_accepted': return 'Open Chat';
      case 'errand_completed': return 'View Details';
      default: return 'View';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'from-green-500 to-green-600 border-green-200';
      case 'warning': return 'from-yellow-500 to-yellow-600 border-yellow-200';
      case 'error': return 'from-red-500 to-red-600 border-red-200';
      default: return 'from-blue-500 to-blue-600 border-blue-200';
    }
  };

  return (
    <div
      className={`transform transition-all duration-500 ease-out ${
        isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
      } cursor-pointer hover:scale-105 transition-transform`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleAction}
    >
      <div className={`bg-white rounded-xl shadow-2xl border-l-4 border-${getTypeColor(notification.type).split(' ')[2]} max-w-sm w-80 overflow-hidden hover:shadow-3xl transition-all`}>
        <div className={`bg-gradient-to-r ${getTypeColor(notification.type).split(' border')[0]} p-1`}></div>
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className={`text-3xl animate-bounce ${isHovered ? 'animate-none' : ''}`}>
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-gray-900 mb-1 truncate">
                  {notification.title}
                </h4>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {notification.message}
                </p>
                {notification.data?.action && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction();
                    }}
                    className={`text-xs bg-gradient-to-r ${getTypeColor(notification.type).split(' border')[0]} hover:opacity-90 text-white px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-medium`}
                  >
                    {getActionText(notification.data.action)} →
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SystemNotificationManagerProps {
  maxVisible?: number;
}

const SystemNotificationManager: React.FC<SystemNotificationManagerProps> = ({
  maxVisible = 5 // Default to 5, but configurable
}) => {
  const { notifications, markAsRead } = useNotifications();
  const [visibleNotifications, setVisibleNotifications] = useState<string[]>([]);

  // Show unread notifications
  useEffect(() => {
    const unreadNotifications = notifications
      .filter(n => !n.is_read)
      .slice(0, maxVisible); // Show configurable number of notifications

    setVisibleNotifications(unreadNotifications.map(n => n.id));
  }, [notifications, maxVisible]);

  const handleClose = (notificationId: string) => {
    setVisibleNotifications(prev => prev.filter(id => id !== notificationId));
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  return (
    <div className="fixed top-4 right-4 z-[70] space-y-3">
      {visibleNotifications.map((notificationId) => {
        const notification = notifications.find(n => n.id === notificationId);
        if (!notification) return null;

        return (
          <SystemNotificationToast
            key={notification.id}
            notification={notification}
            onClose={() => handleClose(notification.id)}
            onMarkAsRead={() => handleMarkAsRead(notification.id)}
          />
        );
      })}
    </div>
  );
};

export default SystemNotificationManager;
